<template>
  <div class="container">
    <ProgressBar
      v-if="isExportLoading"
      :value="exportProgress"
      style="height: 14px"
    />

    <div class="grid align-items-center">
      <p class="col heading-1 my-4">Data Analytics</p>
      <ExportEquipmentxlsx
        :sizings="typedSizings"
        :sizingsWhere="sizingsWhere"
        :aggregatedCounts="aggregatedCounts"
        :selectedEquipmentMap="selectedEquipmentMap"
      />

      <Button
        label="Export Scenario Data"
        class="p-button-success ml-2"
        data-cy="export-scenario-data-button"
        :disabled="sizings.length === 0"
        :loading="isExportLoading"
        @click="handleExportScenarios"
      />
    </div>
    <AdvancedSearch
      v-model:sizings-where="sizingsWhere"
      :is-advanced="true"
      @is-empty="isEmptyHandler"
    />

    <EquipmentSearch
      @update-equipment="updateEquipmentHandler"
      @is-empty="isEquipmentEmptyHandler"
    />
    <div class="card">
      <DataTable
        v-if="!isLoading"
        v-model:expandedRows="expandedRows"
        :value="sizings"
        dataKey="id"
        tableStyle="min-width: 50rem"
        :rows="sizingsLimit"
        :first="sizingsOffset"
        :total-records="sizingsAggregateCount"
        paginator
        lazy
        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        current-page-report-template="Showing {first} to {last} of {totalRecords}"
        @update:first="val => (sizingsOffset = val)"
        @update:rows="val => (sizingsLimit = val)"
      >
        <div>
          <p class="flex flex-wrap gap-2 title">
            <span class="text-xl text-900 font-bold">
              {{ aggregatedCounts.sizingsCount }} Sizings
              {{ aggregatedCounts.scenariosCount }} Scenarios
              {{ aggregatedCounts.performanceEvaluationsCount }} Performance
              Evaluations
            </span>
          </p>
        </div>

        <Column expander></Column>
        <Column header="Title">
          <template #body="slotProps">
            <a :href="linktoSizings(slotProps.data.id)" class="hype">{{
              slotProps.data.title ? slotProps.data.title : ""
            }}</a>
          </template>
        </Column>
        <Column field="customer.customer_name" header="Customer"></Column>

        <Column field="well.well_name" header="Well"></Column>
        <Column
          :field="rowData => formatDate(rowData.journal_created_at)"
          header="Created At"
        ></Column>
        <Column
          :field="rowData => formatDate(rowData.journal_updated_at)"
          header="Updated At"
        ></Column>

        <template #expansion="slotProps">
          <div class="scenario-tabs">
            <TabView>
              <TabPanel
                :header="`Active Scenarios (${slotProps.data.scenarios_aggregate.aggregate.count_active})`"
              >
                <EquipmentItems
                  :scenarios="activeScenarios(slotProps.data.scenarios)"
                />
              </TabPanel>
              <TabPanel
                :header="`Inactive Scenarios (${slotProps.data.scenarios_deleted_aggregate.aggregate.count_deleted})`"
              >
                <EquipmentItems
                  :scenarios="inactiveScenarios(slotProps.data.scenarios)"
                />
              </TabPanel>
            </TabView>
          </div>
        </template>

        <template #footer>
          In total there are {{ aggregatedCounts.sizingsCount }} sizings.
        </template>
      </DataTable>
      <div v-if="isLoading" class="spinner-container">
        <ProgressSpinner />
      </div>
    </div>

    <ExportSizingDialog
      v-if="shouldExportScenarios && exportSizings.length"
      :sizings="exportSizings"
      :shouldExportSizing="shouldExportScenarios"
      @update:should-export-sizing="val => (shouldExportScenarios = val)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { Scenarios, Sizings_Bool_Exp } from "gql/api";
import { useAnalyzeQuery, Order_By } from "gql/api";
import EquipmentSearch from "@/views/DataAnalytics/EquipmentSearch.vue";
import AdvancedSearch from "@/views/DataAnalytics/AdvancedSearch.vue";
import EquipmentItems from "@/views/DataAnalytics/EquipmentItems.vue";
import ExportEquipmentxlsx from "@/views/DataAnalytics/ExportEquipmentxlsx.vue";
import ExportSizingDialog from "@/components/sizing/ExportSizingDialog.vue";

import type { EquipmentItem } from "@/views/DataAnalytics/models/EquipmentSelection";
import DataTable from "primevue/datatable";

import Column from "primevue/column";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import ProgressSpinner from "primevue/progressspinner";
import type { Sizing } from "@/composables/forms/formTypes";

const sizingsLimit = ref(20);
const sizingsOffset = ref(0);
const sizingsOrderBy = ref([{ journal_updated_at: Order_By.Desc }]);
const sizingsWhere = ref<Sizings_Bool_Exp | undefined>(undefined);
const scenariosWhere = ref<Sizings_Bool_Exp | undefined>(undefined);
const isInputFiltersEmpty = ref(true);
const isEquipmentEmpty = ref(true);
const isLoading = ref(false);
const isExportLoading = ref(false);
const EXPORT_LIMIT = 60;
const exportOffset = ref(0);
const exportProgress = ref(0);

const sizingsVariables = computed(() => ({
  withAggregate: true,
  withImportUser: true,
  limit: sizingsLimit.value,
  offset: sizingsOffset.value,
  order_by: sizingsOrderBy.value,
  kitchenSink: true,
  where: sizingsWhere.value || { _and: [] },
  scenarioWhere: scenariosWhere.value || { _and: [] }
}));

const shouldExportScenarios = ref(false);

const linktoSizings = (id: string) => {
  const base = window.location.origin;
  return `${base}/sizings/${id}`;
};

const {
  fetching: sizingsFetching,
  data: sizingsData,
  resume: play,
  pause: stop
} = useAnalyzeQuery({
  requestPolicy: "network-only",
  variables: sizingsVariables,
  pause: true
});

const exportSizings = ref<Sizing[]>([]);

const {
  fetching: exportFetching,
  data: exportData,
  resume: fetchExportPage,
  pause: stopExportFetch
} = useAnalyzeQuery({
  requestPolicy: "network-only",
  variables: computed(() => ({
    ...sizingsVariables.value,
    limit: EXPORT_LIMIT,
    offset: exportOffset.value
  })),
  pause: true
});

const sizingsComp = computed(() => sizingsData.value?.sizings ?? []);

const sizingsAggregateCountComp = computed(
  () => sizingsData.value?.sizings_aggregate?.aggregate?.count ?? 0
);

const sizings = ref(sizingsData.value?.sizings ?? []);

const sizingsAggregateCount = ref(0);

const expandedRows = ref({});

const isEmpty = computed(
  () => isInputFiltersEmpty.value && isEquipmentEmpty.value
);
const typedSizings = computed<Sizing[]>(
  () => (sizings.value ?? []) as Sizing[]
);

const aggregatedCounts = computed(() => {
  if (isEmpty.value || !sizingsData.value) {
    return {
      sizingsCount: 0,
      scenariosCount: 0,
      performanceEvaluationsCount: 0
    };
  }

  return {
    sizingsCount: sizingsData.value.sizings_aggregate?.aggregate?.count ?? 0,
    scenariosCount:
      sizingsData.value.scenarios_aggregate?.aggregate?.count ?? 0,
    performanceEvaluationsCount:
      sizingsData.value.performance_evaluations_aggregate?.aggregate?.count ?? 0
  };
});

watch(
  [
    sizingsComp,
    sizingsAggregateCountComp,
    sizingsWhere,
    isEmpty,
    sizingsFetching
  ],
  ([newSizings, newAggregateCount, newWhere, newIsEmpty, fetching]) => {
    sizings.value = newSizings;
    sizingsAggregateCount.value = newAggregateCount;

    isLoading.value = fetching;

    if (newIsEmpty || !newWhere || Object.keys(newWhere).length === 0) {
      stop();
      sizings.value = [];
      sizingsAggregateCount.value = 0;
    } else {
      play();
    }
  },
  { deep: true, immediate: true }
);

const formatDate = (rowData: string | number | Date) => {
  return new Date(rowData).toLocaleDateString();
};

const isEmptyHandler = (isEmptyStatus: boolean) => {
  isInputFiltersEmpty.value = isEmptyStatus;
};

const isEquipmentEmptyHandler = (isEmptyStatus: boolean) => {
  isEquipmentEmpty.value = isEmptyStatus;
};

const updateEquipmentHandler = (
  equipment: Record<string, EquipmentItem[] | null>
) => {
  let newScenarios = {};
  let wellProperties = {};

  // Reset and populate selectedEquipmentMap
  selectedEquipmentMap.value = {};

  // Iterate over each equipment type and populate selectedEquipmentMap
  Object.keys(equipment).forEach(type => {
    const items = equipment[type];
    if (items && items.length > 0) {
      items.forEach(item => {
        selectedEquipmentMap.value[item.id] = { ...item, type };
      });
    }
  });

  // Build newScenarios and wellProperties based on selected equipment
  if (equipment.pumps && equipment.pumps.length > 0) {
    newScenarios = {
      ...newScenarios,
      xref_pumps: {
        pump_id: { _in: equipment.pumps.map(x => x.id) }
      }
    };
  }

  if (equipment.intakes && equipment.intakes.length > 0) {
    newScenarios = {
      ...newScenarios,
      xref_intakes: {
        intake_id: { _in: equipment.intakes.map(x => x.id) }
      }
    };
  }

  if (equipment.motors && equipment.motors.length > 0) {
    newScenarios = {
      ...newScenarios,
      motor_id: {
        _in: equipment.motors.map(x => x.id)
      }
    };
  }

  if (equipment.cables && equipment.cables.length > 0) {
    newScenarios = {
      ...newScenarios,
      xref_cables: {
        cable_id: { _in: equipment.cables.map(x => x.id) }
      }
    };
  }

  if (equipment.seals && equipment.seals.length > 0) {
    newScenarios = {
      ...newScenarios,
      xref_seals: {
        seal_id: { _in: equipment.seals.map(x => x.id) }
      }
    };
  }

  if (equipment.tubings && equipment.tubings.length > 0) {
    wellProperties = {
      ...wellProperties,
      top_tubing_id: { _in: equipment.tubings.map(x => x.id) },
      bottom_tubing_id: { _in: equipment.tubings.map(x => x.id) }
    };
  }

  if (equipment.casings && equipment.casings.length > 0) {
    wellProperties = {
      ...wellProperties,
      top_casing_id: { _in: equipment.casings.map(x => x.id) },
      bottom_casing_id: { _in: equipment.casings.map(x => x.id) }
    };
  }

  // Update sizingsWhere and scenariosWhere
  sizingsWhere.value = {
    ...sizingsWhere.value,
    scenarios: {
      equipment: {
        ...newScenarios
      },
      ...(Object.keys(wellProperties).length > 0 && {
        well_properties: {
          ...wellProperties
        }
      })
    }
  };

  scenariosWhere.value = {
    ...sizingsWhere.value.scenarios
  };
};

const activeScenarios = (scenarios: Scenarios[]) => {
  return scenarios.filter(scenario => !scenario.journal_deleted_at);
};

const inactiveScenarios = (scenarios: Scenarios[]) => {
  return scenarios.filter(scenario => scenario.journal_deleted_at);
};

const selectedEquipmentMap = ref<
  Record<string, EquipmentItem & { type: string }>
>({});

const handleExportScenarios = async () => {
  if (isExportLoading.value) return;
  isExportLoading.value = true;

  if (typedSizings.value.length === aggregatedCounts.value.sizingsCount) {
    exportSizings.value = typedSizings.value;
  } else {
    const total = aggregatedCounts.value.sizingsCount;
    exportSizings.value = await fetchAllSizingsPaged(total);
  }

  isExportLoading.value = false;
  stopExportFetch();
  shouldExportScenarios.value = true;
};

async function fetchAllSizingsPaged(totalRows: number): Promise<Sizing[]> {
  const all: Sizing[] = [];
  exportOffset.value = 0;
  exportProgress.value = 0;

  while (all.length < totalRows) {
    fetchExportPage();
    const page = await waitForPage();
    all.push(...page);
    exportOffset.value += EXPORT_LIMIT;
    exportProgress.value = Math.min(
      100,
      Math.round((all.length / totalRows) * 100)
    );
  }
  return all;
}

function waitForPage(): Promise<Sizing[]> {
  return new Promise(resolve => {
    let seenLoading = false;
    const stop = watch(
      () => exportFetching.value,
      fetching => {
        if (fetching) {
          seenLoading = true;
        } else if (seenLoading) {
          stop();
          resolve((exportData.value?.sizings ?? []) as Sizing[]);
        }
      }
    );
  });
}
</script>

<style lang="scss" scoped>
.p-3,
.title {
  padding: 1rem;
}
.scenario-tabs {
  margin: 20px;
}

.p-datatable {
  width: 100%;
}

.p-datatable-striped .p-datatable-tbody > tr:nth-of-type(odd) {
  background-color: $white;
}
::v-deep .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
  background-color: $highlightBg;
  color: $white;
}
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
.hype {
  color: var(--info-500) !important;
  &:visited {
    color: var(--info-500);
  }
}
</style>
